<script setup>
    import Layout from "@/layouts/main.vue";
    import PageHeader from "@/components/page-header";
    import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
    import { required, helpers, maxValue } from "@vuelidate/validators";
    import useVuelidate from "@vuelidate/core";
    import { computed, ref, onMounted, onUnmounted } from 'vue';
    import { useStore } from 'vuex';
    import axios from 'axios';
    import { useRoute } from 'vue-router';
    import { authHeader } from './../../../../helpers/authservice/auth-header';

    const state = ref({
        camera_id: null,
        location: '',
        lanes: null,
        camera_type_id: null,
        lgu_id: null,
        is_enabled: false,
        mode_status: null
    });

    const rules = {
        camera_id: {
            required: helpers.withMessage('Camera ID is required', required),
        },
        location: {
            required: helpers.withMessage("Camera Location Name is required", required),
        },
        lanes: {
            required: helpers.withMessage("Camera Lanes is required", required),
            maxValue: helpers.withMessage("Camera Lanes not greater than 32767", maxValue(32767))
        },
        camera_type_id: {
            required: helpers.withMessage("Camera Type is required", required),
        },
        lgu_id: {
            required: helpers.withMessage("LGU is required", required),
        }
    }

    const route = useRoute();
    const store = useStore();
    const v$ = useVuelidate(rules, state);

    const loading = computed(() => store.state.globals.loading);
    const title = ref("Camera Dashboard");
    const items = ref([
        {
            text: "Home",
            href: "/",
        },
        {
            text: "Settings",
            href: "/camera/dashboard/list",
            active: true
        },
        {
            text: "Edit Camera",
            active: true,
        }
    ]);

    const lguOptions = computed(() => store ? store.state.lgu.dropdownLGUs : []);
    const cameraTypeOptions = computed(() => store ? store.state.camera.dropdownCameraTypes: [] );
    const submitted = ref(false);
    const notification = computed(() => store ? store.state.notification : null)

    const updateCamera = async () => {
        submitted.value = true
        v$.value.$touch();
        if (v$.value.$invalid) return;

        try {
            await store.dispatch('camera/updateCamera', state.value);
        } catch (err) {
            console.log(err);
        }
    }

    const getCameraDetails = async () => {
        store.dispatch('globals/loading', null, { root: true });
        try {
            const { data } = await axios.get(`/cameras/${route.params.id}`, {
                headers: authHeader()
            });
            state.value = data.data;
            store.dispatch('globals/loaded', null, { root: true });
        } catch (err) {
            console.log(err);
             store.dispatch('globals/loaded', null, { root: true });
        }
    }

    function getAllLGUs() {
        store.dispatch('lgu/getDropdownLGUs');
    }

    function getAllCameraTypes() {
        store.dispatch('camera/getDropdownCameraTypes');
    }

    onUnmounted(() => {
        store.dispatch('notification/clear', null, { root: true });
        state.value = {
            camera_id: null,
            location: '',
            lanes: null,
            camera_type_id: null,
            lgu_id: null,
            is_enabled: false,
            mode_status: null
        }
    })

    onMounted(async () => {
        await getCameraDetails();
        getAllLGUs();
        getAllCameraTypes();

    })
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
        <div class="col lg-12">
            <b-overlay :show="loading">
            <div class="card">
                <div class="card-header">
                    <h4 class="card-title mb-0">Edit Camera</h4>
                </div>
                <div class="card-body">
                     <b-alert v-if="notification && notification.message"
                        show variant="success" class="fade show" role="alert">
                        {{ notification.message }}
                    </b-alert>

                    <div v-if="notification && notification.messages.length > 0">
                        <b-alert v-for="(message, index) in notification.messages" :key="index"
                            show variant="danger" class="fade show" role="alert">
                            {{ message }}
                        </b-alert>
                    </div>

                    <form @submit.prevent="updateCamera" class="mx-2">
                        <div class="row">
                            <p class="fs-6 text text-decoration-underline">Camera Information</p>
                             <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="camera-location-id" class="form-label required">Camera ID</label>
                                    <input
                                        :class="{ 'is-invalid': submitted && v$.camera_id.$error }"
                                        v-model="state.camera_id" v-maska="'########'" class="form-control" type="text" placeholder="Enter camera location ID..." id="camera-location-ID" />
                                      <div
                                        v-for="item of v$.camera_id.$errors"
                                        :key="item.$uid"
                                        class="invalid-feedback"
                                    >
                                        <span v-if="item.$message">{{ item.$message }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="camera-location-name" class="form-label required">Camera Location Name</label>
                                    <input
                                        :class="{ 'is-invalid': submitted && v$.location.$error }"
                                        v-model="state.location" class="form-control" type="text" placeholder="Enter camera location name..." id="camera-location-name" />
                                      <div
                                        v-for="item of v$.location.$errors"
                                        :key="item.$uid"
                                        class="invalid-feedback"
                                    >
                                        <span v-if="item.$message">{{ item.$message }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="camera-lane" class="form-label required">Camera Lanes</label>
                                    <input
                                        :class="{ 'is-invalid': submitted && v$.lanes.$error }"
                                        v-model="state.lanes" v-maska="'#####'" class="form-control" placeholder="Enter camera lanes..." id="camera-lane" />
                                    <div
                                        v-for="item of v$.lanes.$errors"
                                        :key="item.$uid"
                                        class="invalid-feedback"
                                    >
                                        <span v-if="item.$message">{{ item.$message }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="lguInput" class="form-label">LGU</label>
                                    <select
                                        :class="{ 'is-invalid': submitted && v$.lgu_id.$error }"
                                        class="form-select" v-model="state.lgu_id">
                                        <option :value="lgu.id"  v-for="lgu in lguOptions" :key="lgu.id">
                                            {{ lgu.name }}
                                        </option>
                                    </select>
                                     <div
                                        v-for="item of v$.lgu_id.$errors"
                                        :key="item.$uid"
                                        class="invalid-feedback"
                                    >
                                        <span v-if="item.$message">{{ item.$message }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="mb-3">
                                    <label for="cameraTypeInput" class="form-label required">Camera Types</label>
                                    <select
                                        :class="{ 'is-invalid': submitted && v$.camera_type_id.$error }"
                                        class="form-select" v-model="state.camera_type_id">
                                        <option v-for="(type) in cameraTypeOptions" :value="type.id" :key="type.id">
                                            {{ type.name }}
                                        </option>
                                    </select>
                                    <div
                                        v-for="item of v$.camera_type_id.$errors"
                                        :key="item.$uid"
                                        class="invalid-feedback"
                                    >
                                        <span v-if="item.$message">{{ item.$message }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="row">
                                    <div class="col-md-4">
                                        <div class="mb-3">
                                            <label for="cameraTypeInput" class="form-label">Camera Mode</label>
                                            <div class="">
                                                <select class="form-select form-select-sm border-success  d-inline-block mr-2" id="camera-mode" v-model="state.mode_status" style="width: 100px">
                                                  <option value="2"> Test </option>
                                                  <option value="1"> Live </option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-8">
                                        <div class="mb-3">
                                            <label for="cameraTypeInput" class="form-label">Camera Status</label>
                                            <div class="form-check form-switch form-switch-lg" dir="ltr">
                                                <input type="checkbox" v-model="state.is_enabled" class="form-check-input" id="camera-status" checked="">
                                                <label class="form-check-label" for="customSwitchsizelg">{{ state.is_enabled ? 'Enabled' : 'Disabled' }}</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-12">
                                <div class="text-end">
                                     <router-link to="/camera/dashboard/list" type="submit" class="btn btn-danger mr-3">
                                        <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                                        Cancel
                                     </router-link>
                                    <button type="submit" class="btn btn-success">
                                        <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                                        Update Camera
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            </b-overlay>
        </div>
    </div>
  </Layout>
</template>
